// EserciziView.jsx — amm-v3 (A4, ondata 31): la vista "Bilanci e archivio"
// (pagina 'esercizi') in React, flag-gated (shell_amm_v3_esercizi, default OFF).
//
// Replica 1:1 renderEsercizi di admin-module.js r.2841-2933: stesso titolo e
// sottotitolo, pillole anno, GRID di slot (uno per tipo documento con badge
// Presente/Bozza/Mancante delegato), righe documento (titolo, nota troncata,
// chip allegati, ✎ modifica, 🗑 elimina admin), bottone '+ Carica' per slot,
// sezione 'Altri documenti' col bottone tipo personalizzato.
//
// STATO CONDIVISO col v1 (parità di persistenza): anno su AMM.eserciziAnno
// (r.756, impostato al primo render dall'anno più recente). COMMIT ATOMICO.
//
// CONFINE (dichiarato): il modale documento (editEsercizioDoc, con allegati
// multipart e guardClose) resta v1; il v3 gli passa il PROPRIO onSaved (refresh
// v3) ⇒ mai renderEsercizi diretto dal modale (niente bypass/interceptor).
//
// DEVIAZIONI DICHIARATE:
//  D-A4.3 doppio click su ✎/🗑 di una riga = 1 azione (guardia ref per id).
//  D-A4.5 caricamento fallito ⇒ StatoBox errore + Riprova (mount fresco) o
//         banner + dati conservati (famiglia D-A1.5/D-A3.2). Il v1 mostra un
//         messaggio d'errore inline (per l'archivio, con titolo+pillole).

import React from 'react';
import { AM_NEW_TEXTS } from './api.js';
import {
  AE_TEXTS,
  allegatiChip, notaTronca,
  readEsercizi, apriEditorDoc, eliminaDoc,
} from './esercizi-api.js';

function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function StatoBox({ error, onRetry }) {
  if (error) {
    const msg = error && error.message ? error.message : String(error);
    return (
      <div role="alert" className="bg-white border rounded-lg p-6 text-center" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
        <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{'⚠ ' + msg}</div>
        <button type="button" style={{ marginTop: 12, background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={onRetry}>{AM_NEW_TEXTS.riprova}</button>
      </div>
    );
  }
  return <div className="text-slate-400 text-sm">Caricamento…</div>;
}

export function EserciziView({ win, tick }) {
  const A = win.AMM;
  const [pendingAnno, setPendingAnno] = React.useState(() => A.eserciziAnno);
  const [dati, setDati] = React.useState({ loading: true, error: null, vista: null });
  const [reloadN, setReloadN] = React.useState(0);
  const busyRef = React.useRef({}); // guardia per-id su ✎/🗑 (D-A4.3)

  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readEsercizi(win).then(
      (r) => { if (vivo) setDati({ loading: false, error: null, vista: r }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN, pendingAnno]);

  const vista = dati.vista;
  const reload = () => setReloadN((n) => n + 1); // refresh v3 (parità: v1 re-fetch anni+arch)
  const selezionaAnno = (a) => { A.eserciziAnno = a; setPendingAnno(a); };

  const onEdit = (rec) => {
    const k = 'e' + (rec && rec.id != null ? rec.id : 't');
    if (busyRef.current[k]) return;
    busyRef.current[k] = true;
    try { apriEditorDoc(win, vista.anno, rec, () => { busyRef.current[k] = false; reload(); }); }
    finally { setTimeout(() => { busyRef.current[k] = false; }, 400); } // il modale può chiudersi senza salvare
  };

  const onDelete = (id) => {
    const k = 'd' + id;
    if (busyRef.current[k]) return;
    if (!win.confirm(AE_TEXTS.confirmElimina)) return;
    busyRef.current[k] = true;
    eliminaDoc(win, id, () => { A.toast(AE_TEXTS.toastEliminato); busyRef.current[k] = false; reload(); })
      .catch(() => { busyRef.current[k] = false; });
  };

  const DocRow = (d) => {
    const chip = allegatiChip(d);
    const note = notaTronca(d.note);
    return (
      <div key={d.id} className="flex items-center justify-between gap-2 py-2 border-t first:border-t-0">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{d.titolo || d.tipo}</div>
          {note ? <div className="text-[11px] text-slate-400 truncate">{note}</div> : null}
        </div>
        {chip.ha
          ? <span className="amm-esd-att text-[11px] text-cyan-700 hover:text-cyan-900 cursor-pointer" data-id={d.id} onClick={() => onEdit(d)}>{chip.testo}</span>
          : <span className="text-[11px] text-slate-300">{chip.testo}</span>}
        <div className="flex items-center gap-2 flex-none">
          <span className="amm-esd-edit text-slate-400 hover:text-cyan-700 cursor-pointer text-sm" data-id={d.id} title="Modifica" onClick={() => onEdit(d)}>{'✎'}</span>
          {A.isAdmin() && <span className="amm-esd-del text-slate-400 hover:text-rose-600 cursor-pointer text-sm" data-id={d.id} title="Elimina" onClick={() => onDelete(d.id)}>{'🗑'}</span>}
        </div>
      </div>
    );
  };

  const SlotCard = (s) => {
    const top = s.documenti && s.documenti[0];
    return (
      <div key={s.tipo} className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium">{s.label}</div>
          <V1Html html={A.esdStatoBadge(top)} />
        </div>
        {s.documenti && s.documenti.length
          ? s.documenti.map(DocRow)
          : <div className="text-xs text-slate-400 py-2">{AE_TEXTS.slotVuoto}</div>}
        <button type="button" className="amm-esd-add mt-2 text-xs text-cyan-700 border border-dashed border-cyan-300 rounded px-3 py-1.5 hover:bg-cyan-50" data-tipo={s.tipo} onClick={() => onEdit({ tipo: s.tipo })}>{AE_TEXTS.carica}</button>
      </div>
    );
  };

  return (
    <div data-amm-v3="esercizi">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">{AE_TEXTS.title}</h1>
          <div className="text-xs text-slate-400">{AE_TEXTS.sub}</div>
        </div>
      </div>

      {/* pillole anno — sempre visibili se abbiamo la lista anni (anche in errore archivio) */}
      {vista && vista.anni && (
        <div className="flex gap-2 flex-wrap">
          {vista.anni.map((a) => {
            const on = a === vista.anno;
            return (
              <span
                key={a}
                className={'amm-esd-anno cursor-pointer text-xs px-3 py-1.5 rounded-full border ' + (on ? '' : 'bg-white text-slate-600 border-slate-200')}
                data-anno={a}
                style={on ? { background: '#ecfeff', color: '#0e7490', borderColor: '#a5f3fc', fontWeight: 600 } : undefined}
                onClick={() => selezionaAnno(a)}
              >{a}</span>
            );
          })}
        </div>
      )}

      {/* D-A4.5: errore con dati presenti ⇒ banner + Riprova, dati conservati */}
      {dati.error && vista && (
        <div role="alert" className="mt-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={reload}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      {!vista ? (
        <div className="mt-4"><StatoBox error={dati.error} onRetry={reload} /></div>
      ) : (
        <>
          <div id="esd-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
            {(vista.slots || []).map(SlotCard)}
          </div>
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-400">{AE_TEXTS.altriTitolo}</div>
              <button id="esd-add-custom" type="button" className="bg-cyan-700 hover:bg-cyan-600 text-white text-xs px-3 py-1.5 rounded font-medium" onClick={() => onEdit({ __custom: true })}>{AE_TEXTS.addCustom}</button>
            </div>
            <div className="bg-white border rounded-lg p-4">
              {(vista.extra || []).length
                ? vista.extra.map(DocRow)
                : <div className="text-xs text-slate-400 py-2">{AE_TEXTS.extraVuoto}</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Factory (pattern createAmmV3Resoconto)
export function createAmmV3Esercizi(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0 && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<EserciziView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
